/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  Middleware for calling methods on the optimizely client
 */

import type { ReactSDKClient } from '@optimizely/react-sdk'
import type { Dispatch as StateTypeDispatch, State as StateTypeState } from '@zcode/state-type'
import type { Middleware, Dispatch as ReduxDispatch } from 'redux'
import type { ExtractActions } from 'redux-actions'
import type { activate, track } from './actions'
import { ACTIVATE, TRACK } from './constants'

export type OptimizelyActions = ExtractActions<typeof track | typeof activate>

export type OptimizelyTrackResultType = Promise<ReturnType<ReactSDKClient['track']>>

export type OptimizelyActivateReturnType = Promise<ReturnType<ReactSDKClient['activate']>>

export type OptimizelyResultType = {
  [ACTIVATE]: OptimizelyActivateReturnType
  [TRACK]: OptimizelyTrackResultType
}

export type OptimizelyMiddlewareDispatch = <Action extends OptimizelyActions>(
  action: Action
) => OptimizelyResultType[Action['type']]

// NOTE: all of the actions return a promise that resolves when the optimizely client is ready.
// This means that dispatching actions to optimizely will always be asynchronous, even if the client
// is ready. The benefit is that we never need to know if optimizely is ready or not while within
// redux. On the server we only ever dispatch to redux with asynchronous functions.
export const createOptimizelyMiddleware = <
  State = StateTypeState,
  Dispatch extends ReduxDispatch = StateTypeDispatch,
>(
  optimizely: ReactSDKClient
): Middleware<OptimizelyMiddlewareDispatch, State, Dispatch> => {
  return (_store) => (next) => (action: OptimizelyActions) => {
    switch (action.type) {
      case TRACK: {
        const { eventKey, attributes, tags } = action.payload
        return optimizely
          .onReady()
          .then(() =>
            optimizely.track(eventKey, undefined, attributes, tags)
          ) satisfies OptimizelyTrackResultType
      }
      case ACTIVATE: {
        const { experimentKey } = action.payload
        return optimizely
          .onReady()
          .then(() => optimizely.activate(experimentKey)) satisfies OptimizelyActivateReturnType
      }
      default:
        return next(action)
    }
  }
}
